import { useQueryClient } from '@tanstack/react-query'
import { useConnection } from 'hooks/useConnection'
import useDebounce from 'hooks/useDebounce'
import useIsWindowVisible from 'hooks/useIsWindowVisible'
import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { supportedChainId } from 'utils/supportedChainId'

import { updateChainId } from './reducer'

function useQueryCacheInvalidator() {
  const queryClient = useQueryClient()
  const chainId = useAppSelector((state) => state.application.chainId)

  useEffect(() => {
    queryClient.invalidateQueries({
      predicate: ({ queryKey }) => {
        if (!Array.isArray(queryKey)) return false
        const [namespace] = queryKey

        if (typeof namespace !== 'string') return false

        const chainScopedNamespaces = new Set([
          'v2',
          'v3',
          'islands',
          'islands-subgraph',
          'farms',
          'baults',
          'routing',
          'tokenPrices',
          'analytics',
        ])

        return chainScopedNamespaces.has(namespace)
      },
    })
  }, [chainId, queryClient])
}

export default function Updater(): null {
  const { chainId, wagmiPublicClient } = useConnection()
  const dispatch = useAppDispatch()
  const windowVisible = useIsWindowVisible()

  const [activeChainId, setActiveChainId] = useState(chainId)

  useQueryCacheInvalidator()

  useEffect(() => {
    if (wagmiPublicClient && chainId && windowVisible) {
      setActiveChainId(chainId)
    }
  }, [dispatch, chainId, wagmiPublicClient, windowVisible])

  const debouncedChainId = useDebounce(activeChainId, 100)

  useEffect(() => {
    const chainId = debouncedChainId ? supportedChainId(debouncedChainId) ?? null : null
    dispatch(updateChainId({ chainId }))
  }, [dispatch, debouncedChainId])

  return null
}
